import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Flex, Image } from 'rebass'
import { useNewTranslation } from 'hooks/index'
import _ from 'lodash'

import {
    makeCustomSubmissionContentSelector,
    makeSelectedMediaContentsSelector,
    selectSelectedVerifyTab,
    makeSubmissionContentSelector,
    setMediaData,
    setMediaModalOpen,
    setSelectedMediaIndex,
    SubmissionProgressProps,
    toggleSelectedMediaContent,
    VERIFY_TAB_TYPE,
    selectShowSidebar,
} from 'state/review'
import { State } from 'state'
import { theme } from 'theme'
import MediaViewer, { MediaData } from 'components/MediaViewer'
import { StepState, TaskState } from 'state/tasks'
import { buildCloudinaryUrl } from 'services/cloudinary'
import styled from 'particles/themes/styled'
import { spacing } from 'particles/styles'
import Text from 'particles/Text'
import { coreColors } from 'particles/colors'
import { CSSProperties } from 'styled-components'
import BriefRequirements from '../../common/BriefRequirements'

const CheckboxButton = styled.input`
    &:hover {
        cursor: pointer;
    }
`

interface MediaSelectorCheckboxProps {
    submissionProgress: SubmissionProgressProps
    submissionContentUuid: string
}

const MediaSelectorCheckbox: React.FC<MediaSelectorCheckboxProps> = ({ submissionProgress, submissionContentUuid }) => {
    const dispatch = useDispatch()
    const selectSelectedMediaContents = useMemo(makeSelectedMediaContentsSelector, [])
    const selectedMediaContents = useSelector((state: State) =>
        selectSelectedMediaContents(state, { submissionProgress }),
    )
    const toggleSelectedImage = useCallback(
        (uuid: string) => {
            dispatch(
                toggleSelectedMediaContent({
                    submissionUuid: submissionProgress.uuid,
                    contentUuid: uuid,
                }),
            )
        },
        [submissionProgress],
    )
    return (
        <Box sx={{ textAlign: 'center' }} width="16px" ml={theme.spacing.xxxxs}>
            <CheckboxButton
                type="checkbox"
                checked={
                    selectedMediaContents &&
                    selectedMediaContents.findIndex((file: any) => file.uuid === submissionContentUuid) !== -1
                }
                onChange={() => toggleSelectedImage(submissionContentUuid)}
            />
        </Box>
    )
}

const SelectorContainer = styled(Flex)`
    &:hover {
        cursor: pointer;
    }
`

interface MediaSelectorProps {
    submissionProgress: SubmissionProgressProps
    submissionContent: any // TODO: define correct type
    setSelectedIndex: (idx: number) => void
    isCustomStep: boolean
}

const MediaSelector: React.FC<MediaSelectorProps> = ({
    submissionProgress,
    submissionContent,
    setSelectedIndex,
    isCustomStep,
}) => {
    const selectedVerifyTab = useSelector(selectSelectedVerifyTab(submissionProgress.uuid))

    return (selectedVerifyTab === VERIFY_TAB_TYPE.REVIEW && submissionContent.length > 1) ||
        selectedVerifyTab === VERIFY_TAB_TYPE.DISCUSSION ? (
        <ScrollableThumbnail>
            {submissionContent.map((data: any, index: number) => (
                <SelectorContainer
                    key={index}
                    onClick={() => {
                        setSelectedIndex(index)
                    }}
                    minWidth={100}
                >
                    {!isCustomStep && (
                        <MediaSelectorCheckbox
                            submissionProgress={submissionProgress}
                            submissionContentUuid={data.uuid}
                        />
                    )}
                    <Box width="75px" height="75px">
                        {data.resource_type === 'image' && (
                            <Image
                                src={buildCloudinaryUrl(data)}
                                width="75px"
                                height="75px"
                                sx={{ objectFit: 'contain' }}
                            />
                        )}
                        {data.resource_type === 'video' && (
                            <video width="75px" height="75px" style={{ objectFit: 'contain' }}>
                                <source src={buildCloudinaryUrl(data)} />
                            </video>
                        )}
                    </Box>
                </SelectorContainer>
            ))}
        </ScrollableThumbnail>
    ) : null
}

interface MediaBtnWrapperProps {
    submissionContent: MediaData
    contentSize: string
    selectedIndex?: number
}

const MediaBtnWrapper: React.FC<MediaBtnWrapperProps> = ({ submissionContent, contentSize, selectedIndex = 0 }) => {
    const dispatch = useDispatch()
    const setMedia = useCallback((data: MediaData[]) => dispatch(setMediaData(data)), [])
    const setMediaModal = useCallback((isOpen: boolean) => dispatch(setMediaModalOpen(isOpen)), [])
    const setMediaIndex = useCallback((index: number) => dispatch(setSelectedMediaIndex(index)), [])
    return (
        <Box width={contentSize}>
            <Box p={0} mx="auto">
                <MediaViewer
                    mediaData={submissionContent}
                    showMediaIndex={selectedIndex}
                    showPopup={false}
                    maxWidth={contentSize}
                    maxHeight={contentSize}
                    setModalOpen={setMediaModal}
                    setMediaData={setMedia}
                    setSelectedMediaIndex={setMediaIndex}
                />
            </Box>
        </Box>
    )
}

interface SubmissionContentProps {
    submissionProgress: SubmissionProgressProps
    step: StepState
    task: TaskState
    showCaption?: boolean
    mt?: string
    photoPadding?: string
    showCustomStepTitle?: boolean
}

interface CustomSubmissionContentProps {
    blockData: any // TODO: define correct type
    submissionProgress: SubmissionProgressProps
    showCustomStepTitle?: boolean
}

const CustomSubmissionContent: React.FC<CustomSubmissionContentProps> = ({
    blockData,
    submissionProgress,
    showCustomStepTitle = true,
}) => {
    const selectSubmissionContents = useMemo(makeCustomSubmissionContentSelector, [])
    const submissionData = useSelector((state: State) =>
        selectSubmissionContents(state, {
            submissionProgress,
            block: blockData,
        }),
    )
    const isSidebarOpen = useSelector(selectShowSidebar)

    if (!blockData) {
        return null
    }
    const contentSize = isSidebarOpen ? '300px' : '350px'

    let SubmissionComponent
    if (blockData.type === 'photo_submission') {
        SubmissionComponent = submissionData ? (
            <MediaBtnWrapper submissionContent={submissionData} contentSize={contentSize} />
        ) : null
    } else if (blockData.type === 'text_submission') {
        SubmissionComponent = <Text scale="caption400">{submissionData}</Text>
    } else if (blockData.type === 'url_submission') {
        SubmissionComponent = (
            <Box my={theme.spacing.xxs}>
                <a style={{ wordBreak: 'break-all' }} href={submissionData} target="_blank" rel="noopener noreferrer">
                    {submissionData}
                </a>
            </Box>
        )
    } else {
        return null
    }
    return (
        <CustomSubmissionContentContainer width={contentSize}>
            {showCustomStepTitle && <Text scale="bodyS700">{blockData.title}</Text>}
            {SubmissionComponent}
        </CustomSubmissionContentContainer>
    )
}

const PredefinedSubmissionContent: React.FC<SubmissionContentProps> = ({
    submissionProgress,
    step,
    task,
    showCaption = true,
}) => {
    const [newT] = useNewTranslation()
    const [selectedIndex, setSelectedIndex] = useState<number>(0)
    const selectSubmissionContents = useMemo(makeSubmissionContentSelector, [])
    const dispatch = useDispatch()

    const setMedia = useCallback((data: MediaData[]) => dispatch(setMediaData(data)), [])
    const setMediaModal = useCallback((isOpen: boolean) => dispatch(setMediaModalOpen(isOpen)), [])
    const setMediaIndex = useCallback((index: number) => dispatch(setSelectedMediaIndex(index)), [])
    const { submissionContent, captionData, url, video_code, post_title } = useSelector((state: State) =>
        selectSubmissionContents(state, {
            submissionProgress,
            step,
        }),
    )
    const isSidebarOpen = useSelector(selectShowSidebar)
    const contentSize = isSidebarOpen ? '300px' : '350px'
    return (
        <Flex flexDirection="column" sx={{ gap: spacing[400] }}>
            <Flex sx={{ gap: spacing[400] }}>
                <div>
                    {url && (
                        <>
                            <Text scale="caption400" textAlign="left">
                                {newT('submission.table.urlLabel')}
                            </Text>
                            <Box sx={{ textAlign: 'left' }} my={theme.spacing.xxs}>
                                <a
                                    style={{
                                        wordBreak: 'break-all',
                                    }}
                                    href={url}
                                >
                                    {url}
                                </a>
                            </Box>
                        </>
                    )}
                    {video_code && (
                        <>
                            <Text scale="caption400" textAlign="left">
                                {newT('submission.table.videoCodeLabel')}
                            </Text>
                            <Box sx={{ textAlign: 'left' }} my={theme.spacing.xxs}>
                                <a
                                    style={{
                                        wordBreak: 'break-all',
                                    }}
                                    href={video_code}
                                >
                                    {video_code}
                                </a>
                            </Box>
                        </>
                    )}
                    {submissionContent && (
                        <Box width={contentSize} maxHeight={383}>
                            <Box p={0} mx="auto">
                                <MediaViewer
                                    mediaData={submissionContent as MediaData}
                                    showMediaIndex={selectedIndex}
                                    showPopup={false}
                                    maxWidth={contentSize}
                                    maxHeight={contentSize}
                                    setModalOpen={setMediaModal}
                                    setMediaData={setMedia}
                                    setSelectedMediaIndex={setMediaIndex}
                                />
                            </Box>
                        </Box>
                    )}
                </div>

                <div>
                    <PostTitleAndCaption>
                        <Text scale="caption400" color="secondary" textAlign="left">
                            {newT('submission.review.originalCaptionTitle')}
                        </Text>
                        {post_title && (
                            <Text scale="bodyS700" textAlign="left">
                                {post_title}
                            </Text>
                        )}
                        {captionData && showCaption && (
                            <ScrollableCaption height={contentSize}>
                                {captionData.split('\n').map((captionParagraph: string, index: number) => (
                                    <Text key={'caption' + index} textAlign="left" scale="bodyS400">
                                        {captionParagraph}
                                    </Text>
                                ))}
                            </ScrollableCaption>
                        )}
                    </PostTitleAndCaption>
                    <BriefRequirements stepId={step.uuid} task={task} />
                </div>
            </Flex>

            <MediaSelector
                submissionProgress={submissionProgress}
                submissionContent={submissionContent}
                setSelectedIndex={setSelectedIndex}
                isCustomStep={false}
            />
        </Flex>
    )
}

const VerifyContent: React.FC<SubmissionContentProps> = ({
    submissionProgress,
    step,
    task,
    showCaption = true,
    mt = theme.spacing.xxs,
    photoPadding = theme.spacing.xxs,
    showCustomStepTitle = true,
}) => {
    return step.type === 'custom' ? (
        <Box mt={mt} sx={{ textAlign: 'left' }}>
            <CustomSubmissionWrapper>
                <div>
                    {step.data.blocks.map((blockData: any, index: number) => {
                        return (
                            <CustomSubmissionContent
                                key={index}
                                blockData={blockData}
                                submissionProgress={submissionProgress}
                                showCustomStepTitle={showCustomStepTitle}
                            />
                        )
                    })}
                </div>
                <BriefRequirements task={task} stepId={step.uuid} />
            </CustomSubmissionWrapper>
        </Box>
    ) : (
        <PredefinedSubmissionContent
            submissionProgress={submissionProgress}
            step={step}
            showCaption={showCaption}
            mt={mt}
            photoPadding={photoPadding}
            task={task}
        />
    )
}

export default React.memo(VerifyContent)

const ScrollableCaption = styled.div<CSSProperties>`
    height: ${p => p.height || 'auto'};
    flex: 1;
    overflow-y: scroll;
    overflow-x: hidden;
    padding-right: ${spacing[200]};

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background-color: ${coreColors.grey700};
        border-radius: 4px;
    }
`
const ScrollableThumbnail = styled.div`
    display: flex;
    overflow-x: scroll;
    overflow-y: hidden;
    padding-bottom: ${spacing[100]};
    width: 100%;
    &::-webkit-scrollbar {
        height: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background-color: ${coreColors.grey700};
        border-radius: 4px;
    }
`
const CustomSubmissionWrapper = styled.div`
    display: flex;
    gap: ${spacing[400]};
`

const CustomSubmissionContentContainer = styled.div<{ width: string }>`
    width: ${({ width }) => width};
`

const PostTitleAndCaption = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing[300]};
    height: 100%;
`
